<script>
import Icon from '@/components/Icon'
import ContentLoading from '@/components/ContentLoading'
import { mapActions } from 'vuex'

export default {
  name: 'ProductImage',

  components: {
    Icon,
    ContentLoading,
  },

  props: {
    src: {
      type: String,
      default: null,
    },

    width: {
      type: [Number, String],
      default: 48,
    },

    height: {
      type: [Number, String],
      default: 54,
    },
  },

  data () {
    return {
      imageLoaded: false,
      imageError: false,
      shouldLoad: false,
      observer: null,
      isLoading: false,
      timeoutId: null,
      authenticatedImageUrl: null, // Blob URL gerada após buscar imagem autenticada
    }
  },

  computed: {
    wrapperStyle () {
      return {
        width: this.normalizeSize(this.width),
        height: this.normalizeSize(this.height),
      }
    },

    shouldShowPlaceholder () {
      return !this.src || this.imageError
    },

    imageSrc () {
      // Se tiver blob URL autenticada, usa ela
      if (this.authenticatedImageUrl) {
        return this.authenticatedImageUrl
      }
      // Se shouldLoad for true e src existir, verifica se precisa autenticação
      if (this.shouldLoad && this.src) {
        // Se já é uma blob URL (começa com blob:), usa diretamente
        if (this.src.startsWith('blob:')) {
          return this.src
        }
        // Se for uma URL HTTP/HTTPS, precisa buscar autenticada
        if (this.src.startsWith('http')) {
          // Retorna null aqui, a imagem será carregada via fetchAuthenticatedImage
          return null
        }
        // Outros casos (data URLs, etc) usa diretamente
        return this.src
      }
      return null
    },

    shouldShowLoading () {
      return this.isLoading && !this.imageLoaded && !this.imageError
    },

    needsAuthentication () {
      // Verifica se a URL precisa de autenticação
      return this.src && this.src.startsWith('http') && !this.src.startsWith('blob:')
    },
  },

  mounted () {
    // Se não há src, não precisa observar
    if (!this.src) {
      return
    }

    // Verifica se Intersection Observer está disponível
    if ('IntersectionObserver' in window) {
      this.setupIntersectionObserver()
    } else {
      this.shouldLoad = true
      // Inicia o loading imediatamente para navegadores antigos
      if (this.src) {
        this.startLoading()
      }
    }
  },

  beforeDestroy () {
    // Limpa o observer quando o componente for destruído
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    // Limpa o timeout se existir
    this.clearTimeout()
    // Limpa blob URL para evitar vazamento de memória
    this.revokeBlobUrl()
  },

  watch: {
    src () {
      // Limpa blob URL anterior antes de mudar
      this.revokeBlobUrl()

      // Reset estados quando o src mudar
      this.imageLoaded = false
      this.imageError = false
      this.shouldLoad = false
      this.isLoading = false
      this.authenticatedImageUrl = null
      this.clearTimeout()

      // Se o src mudar e já tiver observer, reconecta
      if (this.src && 'IntersectionObserver' in window) {
        this.$nextTick(() => {
          if (this.observer) {
            this.observer.disconnect()
          }
          this.setupIntersectionObserver()
        })
      } else if (this.src && !('IntersectionObserver' in window)) {
        this.shouldLoad = true
        // Inicia o loading imediatamente para navegadores antigos
        this.startLoading()
      }
    },

    shouldLoad (newValue) {
      // Quando shouldLoad vira true, inicia o loading
      if (newValue && this.src && !this.imageLoaded && !this.imageError) {
        this.startLoading()
      }
    },
  },

  methods: {
    ...mapActions('products', ['getProductImage']),

    normalizeSize (value) {
      if (value === null || value === undefined || value === '') {
        return undefined
      }

      if (typeof value === 'number') {
        return `${value}px`
      }

      return value
    },

    setupIntersectionObserver () {
      if (!this.$el) {
        return
      }

      // Opções do Intersection Observer
      const options = {
        root: null, // viewport como root
        rootMargin: '50px', // Começa a carregar 50px antes de entrar na viewport
        threshold: 0.01, // Dispara quando pelo menos 1% do elemento está visível
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Elemento está visível, pode carregar a imagem
            this.shouldLoad = true
            // Desconecta o observer após começar a carregar
            if (this.observer) {
              this.observer.disconnect()
              this.observer = null
            }
          }
        })
      }, options)

      // Observa o elemento wrapper
      this.observer.observe(this.$el)
    },

    async startLoading () {
      this.isLoading = true
      this.clearTimeout()

      // Se precisa de autenticação, busca a imagem autenticada
      if (this.needsAuthentication) {
        try {
          await this.fetchAuthenticatedImage()
        } catch (error) {
          // Erro já tratado em fetchAuthenticatedImage
        }
      }

      // Timeout de 3000ms
      this.timeoutId = setTimeout(() => {
        if (this.isLoading && !this.imageLoaded) {
          // Se após 3 segundos a imagem não carregou, considera erro
          this.isLoading = false
          this.imageError = true
          this.imageLoaded = false
        }
      }, 3000)
    },

    async fetchAuthenticatedImage () {
      try {
        // Usa a URL diretamente (o serviço agora aceita URL)
        const imageData = await this.getProductImage(this.src)

        if (imageData && imageData instanceof Blob && imageData.size > 0) {
          // Cria blob URL a partir do Blob recebido
          const blobUrl = URL.createObjectURL(imageData)
          this.authenticatedImageUrl = blobUrl

          // Aguarda o próximo tick para garantir que a imagem seja renderizada
          await this.$nextTick()
        } else {
          this.imageError = true
          this.isLoading = false
        }
      } catch (error) {
        // logger.error('Erro ao buscar imagem autenticada:', error)
        this.imageError = true
        this.isLoading = false
        this.clearTimeout()
      }
    },

    revokeBlobUrl () {
      // Revoga blob URL anterior para liberar memória
      if (this.authenticatedImageUrl && this.authenticatedImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.authenticatedImageUrl)
      }
      this.authenticatedImageUrl = null
    },

    clearTimeout () {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId)
        this.timeoutId = null
      }
    },

    handleImageLoad () {
      this.imageLoaded = true
      this.imageError = false
      this.isLoading = false
      this.clearTimeout()
    },

    handleImageError () {
      this.imageError = true
      this.imageLoaded = false
      this.isLoading = false
      this.clearTimeout()
    },
  },
}
</script>

<template>
  <div :class="[$classes['product-image-wrapper'], {
    [$classes['no-image']]: shouldShowPlaceholder
  }]" :style="wrapperStyle">
    <!-- Imagem sempre no DOM quando imageSrc existe para poder carregar -->
    <img v-if="imageSrc && !imageError" :src="imageSrc" :class="$classes['product-image']" @load="handleImageLoad"
      @error="handleImageError" :style="{ opacity: imageLoaded ? 1 : 0 }" loading="lazy" />
    <!-- Loading sobrepõe quando está carregando -->
    <content-loading v-if="shouldShowLoading" :height="'80px'" :class="$classes['overlay']" />
    <!-- Placeholder sobrepõe quando não tem src ou deu erro -->
    <icon v-if="shouldShowPlaceholder && !shouldShowLoading" name="image" :class="$classes['product-image-icon']" />
  </div>
</template>

<style module>
.product-image-wrapper {
  background: #ccc;
  color: #fff;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
}

.product-image-wrapper.no-image {
  min-height: 80px;
}

.product-image-icon {
  width: 18px;
  height: 18px;
}

.product-image {
  display: block;
  width: 100%;
  height: 100%;
  transition: opacity 0.3s ease;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
